import React from 'react';
import { useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import { HbrTag } from '@harbor/elements-react-standalone';
import StbButton from '../../../security-toolbox-ui/StbButton';
import { useModal } from '../../../security-toolbox-ui/StbModalContext';
import { useDeletePovUseCaseMutation } from '../../../../hooks/opportunity-pov-hooks';
import { useFetchOpportunity, useFetchOpportunityProducts } from '../../../../hooks/opportunity-hooks';
import { CacheKey } from '../../../../types/cache-keys';
import { useFetchPov } from '../../opportunityDetailHelpers';
import { Opportunity } from '../../../../types/opportunity';
import { UseCase } from '../../../../types/opportunity-pov-items';
import StbModal from '../../../security-toolbox-ui/StbModal';

type UseCaseWithLineage = UseCase & {
  product_use_case?: number | null;
  is_custom?: boolean;
};

interface Props {
  useCase: UseCaseWithLineage;
  opportunityId: Opportunity['id'];
  editUC: () => void;
  updated: () => void;
}

const DesignPhaseUseCase = ({
                              useCase,
                              opportunityId,
                              editUC,
                              updated,
                            }: Props) => {
  const queryClient = useQueryClient();

  const { data: opportunity } = useFetchOpportunity(opportunityId, { enabled: !!opportunityId });
  const { data: pov } = useFetchPov(opportunity);
  const { data: products } = useFetchOpportunityProducts(opportunityId);
  const { mutateAsync: deleteUseCase } = useDeletePovUseCaseMutation(String(pov?.id));

  const { openModal, closeModal } = useModal();

  const handleOpenDeleteConfirmModal = () => {
    openModal({
      content: () => (
        <StbModal
          open={true}
          role="delete"
          onSubmit={handleDeleteUsecase}
          onClose={async () => closeModal()}
        >
          <div>Are you sure you want to delete this use case?</div>
        </StbModal>
      ),
    });
  };

  const handleDeleteUsecase = async () => {
    await deleteUseCase(useCase as any);
    updated();
    queryClient.invalidateQueries({ queryKey: [CacheKey.PovUseCases, useCase.pov_outcome] });
  };

  const getProductName = (productId?: number): string => {
    if (!productId) {
      return '';
    }
    return products?.find((p) => p.id === productId)?.name ?? '';
  };

  const isCustom = useCase?.is_custom ?? !useCase?.product_use_case;

  return (
    <section className="mb-4 relative border border-gray-400 rounded-md p-4">
      <div className="flex items-center mb-4">
        <h4 className="flex-grow">{useCase.title}</h4>
        <div className="flex-grow-0">
          <StbButton
            iconOnly={true}
            role="update"
            size="small"
            variant="text"
            className="mr-2"
            onClick={editUC}
          />
          <StbButton
            iconOnly={true}
            role="delete"
            size="small"
            variant="text"
            onClick={handleOpenDeleteConfirmModal}
          />
        </div>
      </div>

      <div className="text-sm mb-4">{useCase.description}</div>

      <div className="flex gap-2 flex-wrap items-center">
        {useCase.product && (
          <HbrTag sentiment="success" size="large">
            {getProductName(useCase.product)}
          </HbrTag>
        )}
        <HbrTag sentiment={isCustom ? 'warning' : 'info'} size="small">
          {isCustom ? 'Custom' : 'Catalog'}
        </HbrTag>
      </div>
    </section>
  );
};

export default DesignPhaseUseCase;